// Detect in-app browsers (webviews: Google Search App, Facebook/Instagram, Line,
// WeChat, TikTok, Android WebView…) where OAuth popups fail. In those we hide the
// social buttons and rely on the popup-free email quick-save instead.
export const isInAppBrowser = () => {
    const ua = (navigator.userAgent || '').toLowerCase();

    return /\bgsa\b|fban|fbav|fb_iab|instagram|line\/|micromessenger|tiktok|snapchat|pinterest\/|;\s?wv[);]/.test(ua);
};

/** VALIDATORS **/
export const isEmpty = (obj) => {
    // null and undefined are "empty"
    if (obj == null) return true;
    if (obj === 'undefined' || obj === 'null' || obj === 'false') return true;

    // Boolean are not empty
    if (obj === true || obj === false) return false;

    // Check if Set or Map objects
    if (obj instanceof Set) return obj.size === 0;
    if (obj instanceof Map) return obj.size === 0;

    // Check if is a Integer
    if (!Number.isNaN(parseFloat(obj)) && Number.isFinite(obj)) return false;

    // Assume if it has a length property with a non-zero value
    // that that property is correct.
    if (obj.length > 0) return false;
    if (obj.length === 0) return true;

    // Otherwise, does it have any properties of its own?
    // Note that this doesn't handle
    // toString and valueOf enumeration bugs in IE < 9
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) return false;
    }

    return true;
};

export const isPresent = (obj) => {
    return !isEmpty(obj);
};

// Create the following functions :
// isArray, isObject, isString, isDate, isRegExp, isFunction, isBoolean, isNull, isUndefined
// USage : Utils.is().isArray(myArray);
export const is = () => {
    const exports = {};

    const types = 'Array Object String Date RegExp Function Boolean Null Undefined'.split(' ');

    const type = function () {
        return Object.prototype.toString.call(this)
            .slice(8, -1);
    };

    for (let i = types.length; i--;) {
        exports['is' + types[i]] = ((self) => (obj) => type.call(obj) === self)(types[i]);
    }

    return exports;
};

/** STRING **/
String.prototype.capitalize = function () {
    return this.charAt(0)
        .toUpperCase() + this.slice(1);
};

String.prototype.replaceAt = function (startIndex, originalLength, replacement) {
    return this.substring(0, startIndex) + replacement + this.substring(startIndex + originalLength);
};

/** OBJECT **/
if (!Object.entries) {
    Object.entries = function (obj) {
        const ownProps = Object.keys(obj);
        let i = ownProps.length;
        const resArray = new Array(i);
        while (i--) {
            resArray[i] = [ownProps[i], obj[ownProps[i]]];
        }

        return resArray;
    };
}

export const compact = (object) => {
    const newObject = {};
    Object.keys(object)
        .forEach((key) => {
            const value = object[key];
            if (isPresent(key) && isPresent(value)) {
                newObject[key] = value;
            }
        });
    return newObject;
};

export const sortObjectByKeys = (object, sortingArray) => {
    return Object.keys(object)
        .sort((a, b) => sortingArray.indexOf(a) - sortingArray.indexOf(b))
        .reduce(
            (obj, key) => {
                obj[key] = object[key];
                return obj;
            },
            {}
        );
};

/** ARRAY **/
Array.prototype.limit = function (limit) {
    if (this && !!limit) {
        return this.slice(0, limit);
    } else {
        return this;
    }
};

Array.prototype.remove = function (item) {
    if (this) {
        return this.filter((value) => value !== item);
    }
};

Array.prototype.first = function () {
    return this[0];
};

Array.prototype.last = function () {
    return this[this.length - 1];
};

Array.prototype.isEqualIds = function (other) {
    if (this.length !== other.length) {
        return false;
    }

    return this.every(function (element, index) {
        if (!other[index]) {
            return false;
        }

        return element.id === other[index].id;
    });
};

Array.prototype.uniqBy = function (uniqKey) {
    return [...new Map(this.map((item) => [item[uniqKey], item])).values()];
};

Array.prototype.replace = function (key, newValue) {
    const newArray = [];
    this.forEach(function (oldValue) {
        if (oldValue[key] === newValue[key]) {
            newArray.push(newValue);
        } else {
            newArray.push(oldValue);
        }
    });
    return newArray;
};

Array.prototype.replaceOrAdd = function (key, newValue) {
    const newArray = [];
    let valueNotFound = true;
    this.forEach(function (oldValue) {
        if (oldValue[key] === newValue[key]) {
            newArray.push(newValue);
            valueNotFound = false;
        } else {
            newArray.push(oldValue);
        }
    });
    if (valueNotFound) {
        newArray.push(newValue);
    }
    return newArray;
};

Array.prototype.uniq = function () {
    return this.filter((value, index, array) => array.indexOf(value) === index);
};

Array.prototype.compact = function () {
    return this.filter((val) => isPresent(val));
};

if (!Array.prototype.at) {
    Object.defineProperty(Array.prototype, 'at', {
        configurable: true,
        value(n) {
            // ToInteger() abstract op
            n = Math.trunc(n) || 0;
            // Allow negative indexing from the end
            if (n < 0) n += this.length;
            // OOB access is guaranteed to return undefined
            if (n < 0 || n >= this.length) return undefined;
            // Otherwise, this is just normal property access
            return this[n];
        },
        writable: true
    });
}

if (!Array.prototype.flat) {
    Object.defineProperty(Array.prototype, 'flat', {
        configurable: true,
        value: function flat() {
            const depth = Number.isNaN(arguments[0]) ? 1 : Number(arguments[0]);

            return depth ? Array.prototype.reduce.call(this, function (acc, cur) {
                if (Array.isArray(cur)) {
                    acc.push.apply(acc, flat.call(cur, depth - 1));
                } else {
                    acc.push(cur);
                }

                return acc;
            }, []) : Array.prototype.slice.call(this);
        },
        writable: true
    });
}

/** UTILS **/
export const uuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
});

// Use:
// decodeURIComponent(Utils.urlParam('distance_slider'))
// Utils.urlParam('distance_slider')
export const getUrlParameter = (sParam) => {
    const sPageURL = window.location.search.substring(1);
    const sURLVariables = sPageURL.split('&');
    for (let i = 0; i < sURLVariables.length; i++) {
        const sParameterName = sURLVariables[i].split('=');
        if (sParameterName[0] === sParam) {
            return sParameterName[1];
        }
    }
};

export const parseUrlParams = (url = window.location.search.substring(1), transformIntoObject = false) => {
    let params = {};

    (new URLSearchParams(url)).forEach((name, value) => {
        let paramValue = decodeURIComponent(value);
        const paramName = decodeURIComponent(name);
        if (paramValue.endsWith('[]')) {
            paramValue = paramValue.replace('[]', ''), params[paramValue] || (params[paramValue] = []), params[paramValue].push(paramName);
        } else {
            let b = paramValue.match(/\[([a-z0-9_\/\s,.-])+]$/g);
            if (b) {
                paramValue = paramValue.replace(b, ''), b = b[0].replace('[', '')
                    .replace(']', ''), params[paramValue] || (params[paramValue] = []), params[paramValue][b] = paramName;
            } else {
                params[paramValue] = paramName;
            }
        }
    });

    if (transformIntoObject) {
        const transformParams = {};

        Object.entries(params)
            .forEach(([name, value]) => {
                const b = name.match(/^(\w+)\[(\w+)]$/);
                if (b && b.length === 3) {
                    transformParams[b[1]] ||= {};
                    transformParams[b[1]][b[2]] = value;
                } else {
                    transformParams[name] = value;
                }
            });

        params = transformParams;
    }

    return params;
};

export const arraySearchForValue = (arr, val, key) => {
    for (let i = 0; i < arr.length; i++) {
        if (arr[i][key] === val) {
            return arr[i];
        }
    }
    return false;
};

// Array must be sorted
export const getClosestValue = (array, value) => {
    if (isEmpty(array)) return;

    let lo = -1;
    let hi = array.length;
    while (hi - lo > 1) {
        const mid = Math.round((lo + hi) / 2);
        if (array[mid] <= value) {
            lo = mid;
        } else {
            hi = mid;
        }
    }
    if (array[lo] === value) hi = lo;

    const lowDiff = Math.abs(array[lo] - value);
    const highDiff = Math.abs(array[hi] - value);

    if (lowDiff < highDiff) {
        return array[lo];
    } else {
        return array[hi];
    }
};

export const formatTime = (totalMin, showZeroHours) => {
    let hours = Math.floor(Math.floor(totalMin) / 60);
    let min = Math.floor(totalMin) % 60;
    let sec = Math.floor((totalMin - Math.floor(totalMin)) * 60.0);

    if (sec === 60) {
        sec = 0;
        min++;
    }

    if (min === 60) {
        min = 0;
        hours++;
    }

    let secStr = sec.toString();

    if (sec < 10) {
        secStr = '0' + secStr;
    }

    let minStr = min.toString();

    if (min < 10) {
        minStr = '0' + minStr;
    }

    if (showZeroHours || hours > 0) {
        return hours + ':' + minStr + ':' + secStr;
    } else {
        return minStr + ':' + secStr;
    }
};

export const flooredNum = (number, decimals) => {
    const multiplier = Math.pow(10, decimals);
    return Math.floor((number) * multiplier) / multiplier;
};

export const NAVIGATION_KEYMAP = {
    //8: 'backspace',
    9: 'tab',
    13: 'enter',
    16: 'shift',
    17: 'ctrl',
    18: 'alt',
    20: 'capslock',
    27: 'esc',
    32: 'space',
    33: 'pageup',
    34: 'pagedown',
    35: 'end',
    36: 'home',
    37: 'left',
    38: 'up',
    39: 'right',
    40: 'down',
    45: 'ins',
    46: 'del',
    91: 'meta',
    93: 'meta',
    224: 'meta'
};

export const debounce = (func, wait, immediate, context) => {
    let result;
    let timerId = null;

    function cancel() {
        if (timerId !== undefined) {
            clearTimeout(timerId);
        }
    }

    function debounced() {
        const ctx = context || this;
        const args = arguments;
        const later = function () {
            timerId = null;
            if (!immediate) result = func.apply(ctx, args);
        };
        const callNow = immediate && !timerId;
        clearTimeout(timerId);
        timerId = setTimeout(later, wait);
        if (callNow) result = func.apply(ctx, args);
        return result;
    }

    debounced.cancel = cancel;

    return debounced;
};

export const throttle = (func, timeFrame) => {
    let lastTime = 0;

    return function (...args) {
        const now = new Date();
        if (now - lastTime >= timeFrame) {
            func(...args);
            lastTime = now;
        }
    };
};

/** BROWSER **/
export const scrollTo = (elementId, margin = 40, behavior = 'smooth') => {
    const element = document.getElementById(elementId);
    if (!element) {
        return;
    }

    window.scroll({
        top: element.offsetTop - margin,
        behavior: behavior
    });
};

export const forcePageReload = (url = window.location) => {
    // Add timestamp to ensure page is not cached
    const timestamp = Date.now();
    const urlParams = window.location.search;
    const newUrl = url + (urlParams ? urlParams + '&' : '?') + `_=${timestamp}`;
    window.location.replace(newUrl);
};

export const clearForceReloadParam = () => {
    if (window.location.search.includes('_=')) {
        const url = new URL(window.location);
        url.searchParams.delete('_');
        window.history.replaceState(null, null, url);
    }
};

export const extractDataFromElement = (elementId, extractAndRemove = true) => {
    const componentElement = document.getElementById(elementId);
    const componentData = {};

    if (!componentElement || !componentElement.attributes) {
        return componentData;
    }

    [].forEach.call(componentElement.attributes, function (componentAttribute) {
        if (/^data-/.test(componentAttribute.name)) {
            const dataName = componentAttribute.name.substr(5)
                .replace(/-(.)/g, function ($0, $1) {
                    return $1.toUpperCase();
                });
            componentData[dataName] = componentAttribute.value.startsWith('{') || componentAttribute.value.startsWith('[') ? JSON.parse(componentAttribute.value) : componentAttribute.value;
        }
    });

    if (extractAndRemove) {
        Object.keys(componentData)
            .filter((componentKey) => componentKey !== 'component')
            .forEach((componentKey) => delete componentElement.dataset[componentKey]);
    }

    return componentData;
};

export const getCookie = (name) => {
    const value = '; ' + document.cookie;
    const parts = value.split('; ' + name + '=');
    if (parts.length === 2) {
        return parts.pop()
            .split(';')
            .shift();
    } else {
        return null;
    }
};

export const addMultipleEventListener = (selector, events, eventHandler, removeEvent = false) => {
    if (!selector) {
        return null;
    }

    events = Array.isArray(events) ? events : events.split(' ');

    events.forEach(function (eventName) {
        if (removeEvent) {
            if (selector instanceof NodeList) {
                selector.forEach((s) => s.removeEventListener(eventName, eventHandler));
            } else {
                selector.removeEventListener(eventName, eventHandler);
            }
        }

        if (selector instanceof NodeList) {
            selector.forEach((s) => s.addEventListener(eventName, eventHandler));
        } else {
            selector.addEventListener(eventName, eventHandler);
        }
    });
};

NodeList.prototype.first = function () {
    return this[0];
};

NodeList.prototype.last = function () {
    return this[this.length - 1];
};

export const getPreviousElement = (element, selector) => {
    if (!selector || !element) {
        return null;
    }

    if (selector) {
        let previous = element.previousElementSibling;
        while (previous && !previous.matches(selector)) {
            previous = previous.previousElementSibling;
        }
        return previous;
    } else {
        return element.previousElementSibling;
    }
};

export const getNextElement = (element) => {
    if (!element) {
        return null;
    }

    return element.nextElementSibling;
};

export const onPageReady = (callback, timeout = undefined) => {
    if ('requestIdleCallback' in window) {
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            // call on next available tick
            if (timeout) {
                setTimeout(callback, timeout);
            } else {
                window.requestIdleCallback(callback);
            }
        } else {
            document.addEventListener('DOMContentLoaded', () => {
                if (timeout) {
                    setTimeout(callback, timeout);
                } else {
                    window.requestIdleCallback(callback);
                }
            });
        }
    } else {
        let timeoutReference;

        document.addEventListener('beforeunload', () => {
            clearTimeout(timeoutReference);
        });

        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            // call on next available tick
            timeoutReference = setTimeout(callback, timeout);
        } else {
            document.addEventListener('DOMContentLoaded', () => timeoutReference = setTimeout(callback, timeout));
        }
    }
};

export const onPageLoaded = (callback, delay = 0) => {
    const whenIdle = () => {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(callback, {timeout: 3000});
        } else {
            callback();
        }
    };
    const start = () => setTimeout(whenIdle, delay);

    if (document.readyState === 'complete') {
        start();
    } else {
        window.addEventListener('load', start, {once: true});
    }
};

// Hand the main thread back to the browser so a pending tap can be handled between two chunks of work.
export const letBrowserHandleInput = () => {
    if (globalThis.scheduler?.yield) {
        return globalThis.scheduler.yield();
    }

    return new Promise((resolve) => {
        setTimeout(resolve, 0);
    });
};
